// Palette commands of the maker workflow: Open makers, New brief, Submit delivery, Members and access.
// Pure data except for `run`, so the shell only has to add them to its list.
import type { Api } from "../../api.ts";
import { loadPicks } from "../../auth.ts";
import { atLeast } from "../../lib.ts";
import { makersApi } from "../../makers-api.ts";
import type { Route } from "../../route.ts";
import type { Project, Role } from "../../types.ts";
import type { PaletteEntry } from "../Palette.tsx";

export interface CommandInput {
  role: Role | undefined;
  go: (r: Route) => void;
  projects: Project[];
  /** the project we are in, or null on the home screen */
  scope: string | null;
  api: Api;
  ws: string;
}

/** The project a command without a project of its own acts on: this one, the last opened, or the only one. */
export function pickProject(
  projects: readonly Project[],
  scope: string | null,
  last: string | null,
): string | null {
  if (scope && projects.some((p) => p.id === scope)) return scope;
  if (last && projects.some((p) => p.id === last)) return last;
  return projects.length === 1 ? projects[0]!.id : null;
}

/** Ids of the commands above, so a screen that knows its project can swap in its own. */
export const MAKER_COMMAND_IDS = new Set([
  "cmd:makers",
  "cmd:new-brief",
  "cmd:submit-delivery",
  "cmd:members",
]);

export function makerCommands(i: CommandInput): PaletteEntry[] {
  const out: PaletteEntry[] = [];
  const member = atLeast(i.role, "maker");
  if (!member) return out;
  const project = pickProject(i.projects, i.scope, loadPicks().project ?? null);
  const makersAt = (
    pid: string,
    page?: { t: "brief"; id: string } | { t: "assignment"; id: string },
  ): Route => ({
    view: "project",
    projectId: pid,
    loc: { kind: "makers", ...(page ? { page } : {}) },
  });

  if (i.role === "maker" && !i.scope) {
    out.push({
      id: "cmd:makers",
      group: "Makers",
      title: "Open my assignments",
      icon: "people",
      route: { view: "home" },
    });
  } else if (project) {
    out.push({
      id: "cmd:makers",
      group: "Makers",
      title: "Open makers",
      hint: i.projects.find((p) => p.id === project)?.name,
      icon: "people",
      route: makersAt(project),
    });
  }

  if (atLeast(i.role, "producer") && project) {
    out.push({
      id: "cmd:new-brief",
      group: "Makers",
      title: "New brief",
      hint: i.projects.find((p) => p.id === project)?.name,
      icon: "plus",
      route: makersAt(project, { t: "brief", id: "new" }),
    });
  }

  if (i.role === "maker") {
    out.push({
      id: "cmd:submit-delivery",
      group: "Makers",
      title: "Submit delivery",
      hint: "your open assignment",
      icon: "upload",
      run: () => {
        void makersApi(i.api, i.ws)
          .listAssignments({ mine: true })
          .then(
            (list) => {
              const open = list.find((a) =>
                ["in_progress", "changes_requested", "accepted"].includes(a.status),
              );
              if (open) i.go(makersAt(open.project_id, { t: "assignment", id: open.id }));
              else i.go({ view: "home" });
            },
            () => i.go({ view: "home" }),
          );
      },
    });
  }

  if (atLeast(i.role, "producer"))
    out.push({
      id: "cmd:members",
      group: "Go",
      title: "Members and access",
      icon: "people",
      route: { view: "settings", page: "members" },
    });
  return out;
}
